import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'
import { storage } from '../utils/env'
import { useLocalization } from '../contexts/Language/LanguageProvider'

const STORAGE_KEY = 'cookie_consent'
const OPEN_SETTINGS_EVENT = 'open-privacy-settings'
// How long to wait for Google's consent message (AdSense Privacy & messaging) before falling back
const CMP_TIMEOUT_MS = 2500

type ConsentValue = 'granted' | 'denied'

type TCData = {
  gdprApplies?: boolean
  eventStatus?: string
  purpose?: { consents?: Record<string, boolean> }
}

declare global {
  interface Window {
    __tcfapi?: (command: string, version: number, callback: (data: TCData, success: boolean) => void) => void
    googlefc?: { showRevocationMessage?: () => void }
  }
}

function updateGoogleConsent(storageConsent: ConsentValue, personalization: ConsentValue = storageConsent) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: storageConsent,
      ad_storage: storageConsent,
      ad_user_data: personalization,
      ad_personalization: personalization,
    })
  }
}

// Mirror the visitor's choice in Google's message into Consent Mode, so the Google tags follow it
function syncConsentFromTCF(data: TCData) {
  const consents = data.purpose?.consents ?? {}
  const canStore: ConsentValue = consents['1'] ? 'granted' : 'denied'
  const canPersonalize: ConsentValue = consents['1'] && consents['3'] && consents['4'] ? 'granted' : 'denied'
  updateGoogleConsent(canStore, canPersonalize)
}

// Resolves with whether GDPR applies to this visitor, or null if Google's message never loads
// (outside the regions it covers it still loads, and reports gdprApplies: false)
function detectGoogleConsentMessage(): Promise<boolean | null> {
  return new Promise(resolve => {
    const startedAt = Date.now()
    const poll = () => {
      if (typeof window.__tcfapi === 'function') {
        window.__tcfapi('addEventListener', 2, (data, success) => {
          if (!success) return resolve(null)
          if (data.gdprApplies && (data.eventStatus === 'tcloaded' || data.eventStatus === 'useractioncomplete')) {
            syncConsentFromTCF(data)
          }
          resolve(!!data.gdprApplies)
        })
      } else if (Date.now() - startedAt > CMP_TIMEOUT_MS) {
        resolve(null)
      } else {
        setTimeout(poll, 100)
      }
    }
    poll()
  })
}

// Footer "Privacy and cookie settings" link
export function openPrivacySettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT))
}

const CookieBanner = () => {
  const { locals } = useLocalization()
  const [visible, setVisible] = useState(false)
  const [gdprApplies, setGdprApplies] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    detectGoogleConsentMessage().then(applies => {
      if (cancelled) return
      setGdprApplies(applies)
      // Visitors covered by Google's message answer there; everyone else gets this banner
      if (applies) return
      const stored = storage.get(STORAGE_KEY)
      if (stored === 'granted' || stored === 'denied') {
        // Re-apply saved choice on every page load so Consent Mode is always in sync
        updateGoogleConsent(stored)
      } else {
        setVisible(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const open = () => {
      if (gdprApplies && window.googlefc?.showRevocationMessage) {
        window.googlefc.showRevocationMessage()
      } else {
        setVisible(true)
      }
    }
    window.addEventListener(OPEN_SETTINGS_EVENT, open)
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open)
  }, [gdprApplies])

  const handleAccept = () => {
    storage.set(STORAGE_KEY, 'granted')
    updateGoogleConsent('granted')
    setVisible(false)
  }

  const handleDecline = () => {
    storage.set(STORAGE_KEY, 'denied')
    updateGoogleConsent('denied')
    setVisible(false)
  }

  if (!visible) return null

  return (
    <Banner role="dialog" aria-label="Cookie consent">
      <Text>
        {locals.cookieBannerText}{' '}
        <PolicyLink to="/privacy-policy">{locals.privacyPolicy}</PolicyLink>.
      </Text>
      <Buttons>
        <DeclineButton onClick={handleDecline}>{locals.cookieEssential}</DeclineButton>
        <AcceptButton onClick={handleAccept}>{locals.cookieAccept}</AcceptButton>
      </Buttons>
    </Banner>
  )
}

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(15, 15, 15, 0.97);
  border-top: 1px solid rgba(255, 215, 0, 0.25);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  animation: ${slideUp} 0.35s ease-out;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem 1.25rem;
  }
`

const Text = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
  line-height: 1.5;
  flex: 1;
`

const PolicyLink = styled(Link)`
  color: #ffd700;
  text-decoration: underline;
  &:hover {
    color: #ffe566;
  }
`

const Buttons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 100%;
    justify-content: flex-end;
  }
`

const BaseButton = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
`

const DeclineButton = styled(BaseButton)`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.65);
  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    color: rgba(255, 255, 255, 0.9);
  }
`

const AcceptButton = styled(BaseButton)`
  background: #ffd700;
  color: #111;
  &:hover {
    background: #ffe566;
  }
`

export default CookieBanner
